import {
  computed, ref, nextTick, watch,
} from 'vue';
import { fireTag } from '@vue-util/tagManager';
import { brand } from 'config';
import useFireTagAsync from '@modules/discovery/composables/useFireTagAsync';
import config from '../utils/config';

import useSortFilter from './useSortFilter';
import useFacets from './useFacets';
import useFacetTypes from './useFacetTypes';
import useAccordionTabs from './useAccordionTabs';
import usePreventAnimation from './usePreventAnimation';

export default function useSortAndFilter() {
  const useFacetsLocal = useFacets();

  const { preventAnimation } = usePreventAnimation();

  const {
    triggerSelectedFacetsUpdate,
    clearAllSelectedValues,
    replaceSelectedValues,
    updateSortbySelected,
    closeDrawer,
    globalOptions,
    isMobile,
    isDesktop,
    isTablet,
    isFacetsLoading,
    isMobileInLandscape,
    isSlideoutMode,
    setOpenedDropdown,
    facets,
  } = useFacetsLocal;

  const { getFacetTypeUtil } = useFacetTypes();

  const { isSYS, isDynamicFilters } = globalOptions;

  const accordionEl = ref(null);
  const {
    resetSelections,
    slideoutProductCount,
    slideoutFacetSelectedCount,
    selections,
    clearAllSelectedFacets: clearAllSelectedFacetsCommon,
    accordionActiveIndex,
    setAccordionActiveFacetIndex,
    setUtagFacetDataOnClose,
    allSingleSelectedFacets,
    rightSideDrawer,
    allFiltersButtonClicked,
    setAllFiltersButtonClicked,
  } = useSortFilter();

  const {
    accordionTabs,
    toggleFacetRows,
    facetRowsExpanded,
  } = useAccordionTabs(selections, globalOptions.facetRowsNumber, accordionActiveIndex);

  const closeSlideout = () => {
    resetSelections();
    toggleFacetRows(false);
    setAllFiltersButtonClicked(false);
  };

  const formattedSlideoutProductCount = computed(() => slideoutProductCount?.value?.toLocaleString() || '0');
  const formattedFacetSelectedCount = computed(() => (isSYS.value && slideoutFacetSelectedCount.value > 0 ? slideoutFacetSelectedCount?.value - 1 : slideoutFacetSelectedCount?.value || ''));

  const accordionTabPtOptions = computed(() => ({ transition: { css: !preventAnimation.value } }));

  const isClearButtonDisabled = computed(() => {
    let selectedFacetsLength = selections.value.facets.filter((facet) => facet?.selectedValues?.length > 0).length;
    if (isSYS.value) {
      selectedFacetsLength = selectedFacetsLength === 1; // By Default it should be 1
    } else {
      selectedFacetsLength = selectedFacetsLength === 0;
    }
    return selectedFacetsLength && !formattedFacetSelectedCount.value;
  });

  globalOptions.onFacetClickedEvent = () => {
    setTimeout(() => {
      const accordionActiveElement = accordionEl.value?.$el.querySelector('.p-accordion-tab.p-accordion-tab-active');
      accordionActiveElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 300);
  };

  watch(accordionActiveIndex, () => {
    nextTick(() => {
      const accordionActiveEl = accordionEl.value?.$el.querySelector('.p-accordion-tab.p-accordion-tab-active');
      /* istanbul ignore next  */
      if (accordionActiveEl != null) {
        accordionActiveEl?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    });
  });

  const applyFilterDynamic = () => {
    closeDrawer();
    setOpenedDropdown(null);
  };

  const applyFilter = () => {
    closeDrawer();
    toggleFacetRows(false);
    setOpenedDropdown(null);
    /* istanbul ignore else */
    if (isMobile?.value) {
      nextTick(() => {
        const facetContainerElement = document.getElementsByClassName('facet-container')[0];
        facetContainerElement.scrollLeft = 0;
      });
    }
    clearAllSelectedValues();
    updateSortbySelected(selections.value.sortBySelectedValue);
    selections.value.facets.forEach((facet) => {
      getFacetTypeUtil(facet).onApplyFilter(facet, useFacetsLocal);
      const {
        name, selectedValues, displayName, facetType, selectionOrder, values,
      } = facet;
      const selectedFacet = {
        name, selectedValues, displayName, facetType, selectionOrder, values,
      };
      replaceSelectedValues(selectedFacet);
    });
    triggerSelectedFacetsUpdate();
    const enableAsyncAnalyticsTags = globalOptions?.consumerStore?.state?.envProps?.enableAsyncAnalyticsTags;
    /* istanbul ignore next  */
    if (brand === 'bcom') {
      if (enableAsyncAnalyticsTags) {
        useFireTagAsync(() => fireTag('link', {
          event_name: 'facet filter = Applied',
        }));
      } else {
        fireTag('link', {
          event_name: 'facet filter = Applied',
        });
      }
      /* istanbul ignore else */
      if (globalOptions.mySizesData) {
        globalOptions.mySizesData.value.toggleAppearance = globalOptions.mySizesData.value.toggleValue;
      }
    }
  };

  const selectedValuesListString = (facet) => {
    const showFacetSelectedValuesList = globalOptions.showFacetSelectedValuesList.value;
    let selectedValues = '';
    /* istanbul ignore else */
    if (showFacetSelectedValuesList && facet.facetType !== 'DELIVERY_AND_PICKUP') {
      if (facet?.children?.length) {
        selectedValues = ([].concat(...facet.selectedValues.map((obj) => (obj?.selectedValues.map((val) => val.split('_')[0]))))).join(', ');
      } else {
        selectedValues = facet.selectedValues.map((selectedVal) => facet.values.find((val) => val?.value === selectedVal)?.label || selectedVal).join(', ');
      }
    }
    return selectedValues;
  };

  const clearAllSelectedFacets = (singleClearedFacet) => {
    clearAllSelectedFacetsCommon(getFacetTypeUtil, singleClearedFacet);
  };

  const onFacetTabToggle = (obj) => {
    setAllFiltersButtonClicked(true);
    setAccordionActiveFacetIndex(obj.index);
  };

  const onFacetClose = (obj) => {
    setAccordionActiveFacetIndex(-1);
    setAllFiltersButtonClicked(true);
    setUtagFacetDataOnClose(obj.index);
  };

  const halfModalSlideout = computed(() => (!rightSideDrawer.value ? 'half-modal-slideout' : ''));
  const halfModalSlideoutPadding = computed(() => (!rightSideDrawer.value && accordionTabs.value[accordionActiveIndex.value]?.header !== 'Size' ? 'half-modal-slideout-padding' : ''));
  const sizeDisplay = computed(() => (!rightSideDrawer.value && accordionTabs.value[accordionActiveIndex.value]?.header === 'Size' ? 'size-display' : ''));
  const marginTop = computed(() => (rightSideDrawer.value ? 'margin-top-s' : ''));
  const padding = computed(() => (rightSideDrawer.value ? 'padding-top-m padding-bottom-m' : 'padding-top-s'));

  const criteriaFacet = computed(() => {
    // use facet from selections
    let targetFacet = accordionTabs.value[accordionActiveIndex.value]?.facet;
    if (isDynamicFilters.value) {
      // use store facet
      targetFacet = facets.value.find((f) => f.displayName === targetFacet.displayName);
    }

    return targetFacet;
  });

  return {
    sortFilterSlideOutId: config.sortFilterSlideOutId,
    closeSlideout,
    isClearButtonDisabled,
    clearAllSelectedFacets,
    applyFilter,
    accordionActiveIndex,
    accordionTabs,
    accordionEl,
    selectedValuesListString,
    drawerOptions: globalOptions.drawerOptions,
    isMobile,
    isTablet,
    isDesktop,
    isFacetsLoading,
    formattedSlideoutProductCount,
    formattedFacetSelectedCount,
    onFacetTabToggle,
    onFacetClose,
    preventAnimation,
    accordionTabPtOptions,
    facetRowsExpanded,
    toggleFacetRows,
    allSingleSelectedFacets,
    isMobileInLandscape,
    isSlideoutMode,
    allFiltersButtonClicked,
    rightSideDrawer,
    halfModalSlideout,
    halfModalSlideoutPadding,
    sizeDisplay,
    marginTop,
    padding,
    globalOptions,
    isDynamicFilters,
    applyFilterDynamic,
    criteriaFacet,
  };
}
